import React from 'react';
import { useEffect, useState } from 'react';
import { Card, Col, Form, Row, Button } from 'react-bootstrap';
import { useForm, FormProvider } from 'react-hook-form';
import axios from 'helpers/api';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FacilityBasicInformation from './FacilityBasicInformation';
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import FacilityFooter from './FacilityFooter';
import FacilityMedsInfo from './FacilityMedsInfo';
import FacilityWorkInfo from './FacilityWorkInfo';
import { toast } from 'react-toastify';
import paths, { rootPaths } from '../../../../routes/paths';
import * as mapYourMedsConstant from '../../../common/mapYourMedsConstant.js';
import MaskedInput from 'react-text-mask';
import FormValidations from 'components/FormValidations/FormValidations';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import FormatPhoneNumber from 'components/common/FormatPhoneNumber';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import moment from 'moment';
import { fetchSharedLivingFacilities, fetchUserDependentAssignments } from 'helpers/sharedLivingApi';
import DeactivationReplacementModal from 'components/users/sharedliving/DeactivationReplacementModal';

// Render DOB as MM/DD/YYYY regardless of whether the API returns it as a Date,
// ISO string, or a pre-formatted MM/DD/YYYY value.
const formatDob = (dob) => {
  if (!dob) return '';
  const m = moment(dob, ['YYYY-MM-DD', moment.ISO_8601, 'MM/DD/YYYY', 'MM/DD/YY'], true);
  return m.isValid() ? m.format('MM/DD/YYYY') : '';
};

// SharedLiving: relationship options for caregiver↔patient assignment (SL-013).
const ATHOME_RELATIONSHIP_OPTIONS = [
  { key: 'Select relationship', value: '' },
  { key: 'Spouse', value: 'spouse' },
  { key: 'Child', value: 'child' },
  { key: 'Parent', value: 'parent' },
  { key: 'Caregiver', value: 'caregiver' },
  { key: 'Friend', value: 'friend' },
  { key: 'Other', value: 'other' }
];

const getOptionValue = option => String(option?.value ?? option ?? '');

/**
 * UI-067: map a backend caregiver-create validation message (SL-105, SL-025a)
 * to the inline form-error field it belongs next to. Returns null when the
 * message is generic (we fall back to a toast in that case).
 */
const mapCaregiverBackendError = (msg) => {
  const m = String(msg || '').toLowerCase();
  if (!m) return null;
  if (m.includes('facility') && (m.includes('shared') || m.includes('flag'))) {
    return { facility: msg };
  }
  if (m.includes('patient') && (m.includes('group') || m.includes('belong') || m.includes('facility'))) {
    return { patients: msg };
  }
  if (m.includes('athome_patient') || m.includes('caregiver')) {
    return { patients: msg };
  }
  return null;
};

const getUserDayProgramIds = user => {
  const dayPrograms =
    user?.day_programs ||
    user?.dayPrograms ||
    user?.programs ||
    user?.day_program_ids ||
    user?.dayProgramIds ||
    (String(user?.role_id) === "6" ? user?.facilities : []) ||
    [];

  return dayPrograms
    .map(program =>
      String(
        program?.day_program_id ??
        program?.dayProgramId ??
        program?.id ??
        program?.value ??
        program
      )
    )
    .filter(Boolean);
};

const getUserFacilityIds = user =>
  (user?.facilities || user?.facility_ids || user?.facilityIds || [])
    .map(facility => String(facility?.facility_id ?? facility?.id ?? facility?.value ?? facility))
    .filter(Boolean);

const normalizeDayProgramOptions = (programs, selectedGroupId) =>
  (Array.isArray(programs) ? programs : [])
    .filter(program => Number(program?.is_active) === 1)
    .filter(program => {
      if (!selectedGroupId) {
        return true;
      }

      return Number(program?.group_id) === Number(selectedGroupId);
    })
    .map(program => ({
      key: program.name || program.day_program_name,
      value: program.id || program.day_program_id
    }));

const AddFacilityStaff = (props) => {

  let initialFormData = {
    fname: '',
    lname: '',
    mobile: '',
    zip: '',
    city: null,
    state: '',
    address: '',
    address2: '',
    email: '',
    designation: '',
    fax: '',
    providername: '',
    username: '',
    role: '',
    facility: [],
    password: '',
    cnfpassword: '',
    is_active: 0,
    has_web_access: 1,
    has_mobile_access: 1,
    // Pharmacy roles (<= 3) can pick any group, so start empty — that's what
    // tells the reactive role-list effect "no group picked yet, show only
    // group-independent roles (Pharmacy Admin / Pharmacy Staff)". Role 4+ is
    // locked to their own group, so we seed from localStorage.
    group: Number(JSON.parse(localStorage.getItem("roleId"))) <= 3 ? '' : localStorage.getItem("groupId")
  };
  const locationData = props.dataObj
  const navigate = useNavigate();
  const isSavingRef = React.useRef(false);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [stateoptions, setStateOptions] = useState([]);
  const [roleoptions, setRoleOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [programDisableGroup, setProgramDisabledGroup] = useState(false);
  const [programDisableFacility, setProgramDisabledFacility] = useState(false);
  const [facilityvalue, setFacilityValue] = useState("");
  const [programOptions, setProgramOptions] = useState([]);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [deactivationDependencies, setDeactivationDependencies] = useState(null);
  const [deactivationServerErrors, setDeactivationServerErrors] = useState([]);
  const [staleCandidateBanner, setStaleCandidateBanner] = useState(false);
  const [assignmentReplacements, setAssignmentReplacements] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchAvailable, setSearchAvailable] = useState('');
  const [dayProgramOptions, setDayProgramOptions] = useState([]);
  const [selectedDayPrograms, setSelectedDayPrograms] = useState([]);
  const [searchAvailableDayPrograms, setSearchAvailableDayPrograms] = useState('');
  const [dayProgramsLoading, setDayProgramsLoading] = useState(false);

  const [availableFacilities, setAvailableFacilities] = useState(programOptions);
  // const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedChecked, setSelectedChecked] = useState([]);
  const [selectedAvailableDayPrograms, setSelectedAvailableDayPrograms] = useState([]);
  const [selectedCheckedDayPrograms, setSelectedCheckedDayPrograms] = useState([]);

  // SharedLiving (role 7): patients are fetched reactively from one selected
  // facility, then assigned via a searchable multi-select list.
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState([]);
  // SharedLiving relationship is a backend-only field now — UI defaults to
  // 'caregiver' and is no longer prompted from the admin.
  const [athomeRelationship, setAtHomeRelationship] = useState('caregiver');
  const patientCacheRef = React.useRef({});

  // UI-065: role-7 facility picker is sourced from
  // /facilities/list-sharedliving-facilities/:groupId (SL-104) — pre-filtered
  // to facilities flagged is_sharedliving_facility = 1, never the full list.
  const [caregiverFacilityOptions, setCaregiverFacilityOptions] = useState([]);
  const [caregiverFacilitiesLoading, setCaregiverFacilitiesLoading] = useState(false);
  const [isActiveLoading, setIsActiveLoading] = useState(false);
  const currentRoleId = JSON.parse(localStorage.getItem("roleId"));
  let results = JSON.parse(localStorage.getItem("facilitiesData")) || [];
  let groupResults = JSON.parse(localStorage.getItem("groupsData")) ? JSON.parse(localStorage.getItem("groupsData")) : [];

  // Feature flags for the user being CREATED depend on the target group, not
  // the logged-in user's own group. For role <= 3 (Pharmacy Admin / Staff)
  // the target group is whatever they pick in the Group dropdown. For role
  // 4+ they are locked to their own group, so we fall back to the cached
  // localStorage flags (set at login from /users/profile).
  // Backend access_control mapping: 4 = is_dayprogram_active, 5 = is_sharedliving_active.
  const getGroupFlag = (accessControlValue) => {
    const targetGroupId = formData?.group ? Number(formData.group) : null;
    if (Number(currentRoleId) <= 3 && targetGroupId) {
      const grp = groupResults.find(g => Number(g.value) === targetGroupId);
      if (grp && Array.isArray(grp.access_control)) {
        return grp.access_control.includes(accessControlValue) ? 1 : 0;
      }
    }
    // Fallback for role 4+ (locked to own group) or before a group is selected:
    // use the logged-in user's localStorage flags.
    if (accessControlValue === 4) {
      return Number(JSON.parse(localStorage.getItem("is_dayprogram_active")) || 0);
    }
    if (accessControlValue === 5) {
      // SharedLiving doesn't have a normalized localStorage flag everywhere;
      // also accept the access_control list.
      const ac = localStorage.getItem("accessControl") || '';
      return ac.split(',').map(Number).includes(5) ? 1 : 0;
    }
    return 0;
  };
  const is_dayprogram_active = getGroupFlag(4);
  const is_sharedliving_active_for_group = getGroupFlag(5);

  useEffect(() => {

    function fetchProviderData() {
      setStateOptions([
        { key: 'Select a state', value: '' },
        ...mapYourMedsConstant.STATE_VALUES
      ])

      setGroupOptions([
        { key: 'Select a group', value: '' },
        ...groupResults
      ])

      // Role list is now derived in a reactive effect below — the build runs
      // again whenever the target group's flags change.
      if (locationData != null) {
        let value = locationData;
        setFormData({
          fname: value.first_name,
          lname: value.last_name,
          mobile: FormatPhoneNumber(value.phone),
          zip: value.zip,
          city: value.city,
          state: value.state,
          address: value.address,
          address2: value.address2,
          email: value.email,
          designation: '',
          fax: value.fax,
          providername: '',
          username: value.username,
          role: value.role_id,
          facility: value.facility_id,
          password: value.password,
          cnfpassword: value.password,
          group: value.group_id,
          is_active: value.is_active,
          has_web_access: value.has_web_access ?? 1,
          has_mobile_access: value.has_mobile_access ?? 1
        });
      }

      const initialGroupId = locationData?.group_id || null;
      let filteredPrograms = [];
      if (initialGroupId) {
        filteredPrograms = results.filter(
          (program) => program.group_id === initialGroupId
        );
      }
      const programVar = filteredPrograms.length > 0 ? filteredPrograms : results
      setProgramOptions(programVar);

      if (locationData?.facilities && programVar.length > 0) {
        // Filter programOptions to match facility IDs from locationData
        const selectedFacilityIds = getUserFacilityIds(locationData);
        const preselectedFacilities = programVar.filter(option =>
          selectedFacilityIds.includes(getOptionValue(option))
        );
        setSelectedFacilities(preselectedFacilities);
      }
    }
    // Trigger the fetch
    fetchProviderData();
    handleRoleFieldChange({ target: { value: locationData?.role_id } }, true);
    if (Number(is_dayprogram_active) === 1 &&
        (String(locationData?.role_id) === "5" || String(locationData?.role_id) === "6")) {
      loadDayPrograms(locationData?.group_id);
    }
    if (String(locationData?.role_id) === "7") {
      loadCaregiverFacilities(locationData?.group_id);
      loadCaregiverAssignments(locationData?.id);
    }
  }, []);

  useEffect(() => {
    if (String(formData.role) === "6") {
      return;
    }

    const initialGroupId = Number(localStorage.getItem('roleId')) > 3 ? locationData?.group_id : formData.group;
    const filteredPrograms = Number(localStorage.getItem('roleId')) < 4 ? results.filter(p => p.group_id === Number(initialGroupId)) : results;
    setProgramOptions(filteredPrograms);

    // For role 7 the facility selection is owned by loadCaregiverAssignments
    // (edit) or the admin's manual picks (create) — never auto-reset here.
    if (String(formData.role) === "7") {
      return;
    }

    if (locationData?.facilities && filteredPrograms.length > 0) {
      const selectedFacilityIds = getUserFacilityIds(locationData);
      const preselected = filteredPrograms.filter(p => selectedFacilityIds.includes(getOptionValue(p)));
      setSelectedFacilities(preselected);
    } else {
      setSelectedFacilities([]);
    }
  }, [formData.group]);

  useEffect(() => {
    if (!["5", "6"].includes(String(formData.role)) || dayProgramOptions.length === 0 || !locationData) {
      return;
    }

    const selectedProgramIds = getUserDayProgramIds(locationData);
    const preselectedPrograms = dayProgramOptions.filter(program =>
      selectedProgramIds.includes(getOptionValue(program))
    );

    if (String(formData.role) === "6") {
      setSelectedFacilities(preselectedPrograms);
    } else {
      setSelectedDayPrograms(preselectedPrograms);
    }
  }, [dayProgramOptions, formData.role, locationData]);

  // Reactive role-options list. Recomputes whenever the TARGET group changes
  // so a pharmacy admin picking a group without day-program / SharedLiving
  // enabled no longer sees those role choices. Strict reset (per request): if
  // the currently-selected role is no longer offered for the new group, clear
  // the role field and any role-specific selections so the admin has to
  // re-pick a valid role for the new group.
  useEffect(() => {
    // Pre-group: only the group-independent roles (Pharmacy Admin / Pharmacy
    // Staff) are offered. The admin has to pick a target group before the
    // group-bound roles (Group Admin, Facility Staff, Day Program User,
    // SharedLiving Caregiver) become selectable. Post-group: full list, with
    // role 6 / role 7 disabled when the group's flags don't support them.
    const groupPicked = Boolean(formData?.group);

    const pharmacyOnly = [
      { key: "Pharmacy Admin", value: 2 },
      { key: "Pharmacy Staff", value: 3 },
    ];

    const groupBoundRoles = [
      { key: "Group Admin", value: 4 },
      { key: "Facility Staff", value: 5 },
      { key: "Day Program User", value: 6 },
      // Role 7 is also creator-role-gated: only role <= 4 can create caregivers.
      ...(Number(currentRoleId) <= 4 ? [{ key: "SharedLiving Caregiver", value: 7 }] : [])
    ];

    // Tag the group-flag-gated entries with disabled + a reason suffix once a
    // target group is picked.
    const annotatedGroupBound = groupBoundRoles.map(r => {
      if (Number(r.value) === 6 && !is_dayprogram_active) {
        return { ...r, key: `${r.key} (Day Programs not enabled for this group)`, disabled: true };
      }
      if (Number(r.value) === 7 && !is_sharedliving_active_for_group) {
        return { ...r, key: `${r.key} (SharedLiving not enabled for this group)`, disabled: true };
      }
      return r;
    });

    const baseList = groupPicked ? annotatedGroupBound : pharmacyOnly;

    let roleList = baseList;
    if (Number(currentRoleId) === 3) {
      roleList = roleList.filter(r => r.value !== 2);
    } else if (Number(currentRoleId) >= 4) {
      roleList = roleList.filter(r => r.value !== 2 && r.value !== 3);
    }

    setRoleOptions([{ key: 'Select a Role', value: '' }, ...roleList]);

    // Always reset role + role-specific state whenever the target group
    // changes. Per user decision: simpler than the conditional "only-reset-
    // if-invalid" rule. Edit mode (locationData != null) is exempt — we never
    // want to undo a pre-populated user's role on first render.
    if (locationData == null && formData.role) {
      setFormData(prev => ({ ...prev, role: '' }));
      setSelectedFacilities([]);
      setSelectedAvailable([]);
      setSelectedChecked([]);
      setSearchAvailable('');
      setSelectedDayPrograms([]);
      setSelectedAvailableDayPrograms([]);
      setSelectedCheckedDayPrograms([]);
      setSearchAvailableDayPrograms('');
      setPatientOptions([]);
      setSelectedPatients([]);
      setAtHomeRelationship('caregiver');
      setProgramDisabledFacility(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.group, currentRoleId, is_dayprogram_active, is_sharedliving_active_for_group]);


  useEffect(() => {
    if (formData.username.trim() === '') {
      setUsernameAvailable(null);
      return;
    }
    const timeout = setTimeout(() => {
      checkUsernameAvailability(formData.username);
    }, 500);
    return () => clearTimeout(timeout);
  }, [formData.username]);

  useEffect(() => {
    if (formData.email.trim() === '') {
      setEmailAvailable(null);
      return;
    }
    const timeout = setTimeout(() => checkEmailAvailability(formData.email), 500);
    return () => clearTimeout(timeout);
  }, [formData.email]);


  const checkEmailAvailability = async (email) => {
    if (!email.trim()) {
      setEmailAvailable(null);
      setErrors((prev) => ({ ...prev, email: 'Email is required.' }));
      return;
    }

    setCheckingEmail(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/users/check-email`,
        { email }
      );
      setEmailAvailable(response.data.status);
      setErrors((prev) => ({
        ...prev,
        email: (response.data.status || locationData != null) ? '' : 'This email is already registered.'
      }));
    } catch (error) {
      setEmailAvailable(null);
      setErrors((prev) => ({ ...prev, email: 'Error checking email. Try again.' }));
    }
    setCheckingEmail(false);
  };

  const checkUsernameAvailability = async (username) => {
    if (!username.trim()) {
      setUsernameAvailable(null);
      setErrors((prev) => ({ ...prev, username: 'Username is required.' }));
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/users/check-username`,
        { username }
      );
      setUsernameAvailable(response.data.status);
      setErrors((prev) => ({
        ...prev,
        username: (response.data.status || locationData != null) ? '' : 'This username is already taken.'
      }));
    } catch (error) {
      setUsernameAvailable(null);
      setErrors((prev) => ({ ...prev, username: 'Error checking username. Try again.' }));
    }
    setCheckingUsername(false);
  };

  const handleGroupChange = (event) => {
    const selectedGroupId = parseInt(event.target.value);
    setFormData({
      ...formData,
      [event.target.name]: event.target.value
    });
    const filteredPrograms = results.filter((program) => program.group_id === selectedGroupId);
    setProgramOptions(filteredPrograms);

    if (["5", "6"].includes(String(formData.role))) {
      setSelectedFacilities([]);
      setSelectedAvailable([]);
      setSelectedChecked([]);
      setSearchAvailable('');
      setSelectedDayPrograms([]);
      setSelectedAvailableDayPrograms([]);
      setSelectedCheckedDayPrograms([]);
      setSearchAvailableDayPrograms('');
      if (Number(is_dayprogram_active) === 1) {
        loadDayPrograms(selectedGroupId);
      }
    }

    if (String(formData.role) === "7") {
      // Group changed for a caregiver — reset facility + patient selection;
      // patient pool will reload reactively once a new facility is picked.
      // Also reload the SL-flagged facility list for the new group.
      setSelectedFacilities([]);
      setSelectedAvailable([]);
      setSelectedChecked([]);
      setSearchAvailable('');
      setPatientOptions([]);
      setSelectedPatients([]);
      patientCacheRef.current = {};
      loadCaregiverFacilities(selectedGroupId);
    }
  };

  const onSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    const selectedIds = selectedFacilities?.map(f => f.value) || [];
    const selectedDayProgramIds = selectedDayPrograms?.map(program => program.value) || [];
    const selectedPatientIds = selectedPatients?.map(p => p.value) || [];
    let facilityStaffData = {
      "user_id": locationData?.id || '',
      "first_name": formData.fname,
      "last_name": formData.lname,
      "email": formData.email,
      "phone": formData.mobile?.replace(/\D/g, '') || '',
      "city": formData.city || '',
      "role_id": formData.role || '',
      "group_id": formData.group || (localStorage.getItem("groupId") || ""),
      "state": formData.state,
      "zip": formData.zip,
      "address": formData.address,
      "address2": formData.address2,
      "username": formData.username,
      "password": formData.password,
      ...(String(formData.role) === "6"
        ? { "day_programs": selectedIds, "facilities": [] }
        : String(formData.role) === "7"
          // SharedLiving caregiver: no facilities/day programs of their own —
          // the backend (SL-025a/SL-025b) creates the user and assigns the
          // patients from athome_patient_ids in a single call.
          ? {
              "facilities": [],
              "day_programs": [],
              "athome_patient_ids": selectedPatientIds,
              // Relationship is not surfaced in the UI — default every
              // SharedLiving assignment to 'caregiver' on the backend.
              "athome_relationship": athomeRelationship || 'caregiver'
            }
          : String(formData.role) === "5"
            ? { "facilities": selectedIds, "day_programs": selectedDayProgramIds }
            : { "facilities": selectedIds, "day_programs": [] }),
      // 'facilities': selectedIds,
      "is_active": formData.is_active == true ? 1 : 0,
      ...(String(formData.role) === "5"
        ? {
            "has_web_access": formData.has_web_access == true ? 1 : 0,
            "has_mobile_access": formData.has_mobile_access == true ? 1 : 0
          }
        : {})
    }
    
    if (assignmentReplacements && formData.is_active == false) {
      facilityStaffData.assignment_replacements = assignmentReplacements;
    }

    let tokenStr = localStorage.getItem("accessToken");

    console.log("userData " + JSON.stringify(facilityStaffData));

    let facilityStaffUrl = locationData != null ? `${process.env.REACT_APP_SERVER_URL}/users/update` : `${process.env.REACT_APP_SERVER_URL}/users/create`;

    try {
      const { data } = await axios.post(facilityStaffUrl, facilityStaffData, { headers: { 'Authorization': `Bearer ${tokenStr}` } });

      console.log("data " + JSON.stringify(data));

      if (data.status) {
        toast.success(data.message);
        isSavingRef.current = true;
        navigate(paths.viewFacility);
      }
      else {
        // UI-067: surface SL-105 caregiver validation errors inline next to the
        // offending field. Backend messages like "patient must belong to
        // group", "facility is not flagged as SharedLiving", etc.
        const msg = String(data.message || '');
        const inline = mapCaregiverBackendError(msg);
        if (inline) {
          setErrors(prev => ({ ...prev, ...inline }));
        } else {
          toast.error(msg || 'Unable to save user');
        }
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        const errorData = err.response.data;
        if (errorData.errorCode === 'REPLACEMENT_REQUIRED' || errorData.errorCode === 'REPLACEMENT_INVALID') {
          if (errorData.errorCode === 'REPLACEMENT_INVALID' && errorData.errors) {
            setDeactivationServerErrors(errorData.errors);
            
            // Re-fetch dependencies (FE7)
            if (locationData?.id) {
              try {
                const refreshed = await fetchUserDependentAssignments(locationData.id);
                setDeactivationDependencies(refreshed);
                setStaleCandidateBanner(true);
              } catch (e) {
                console.error("Failed to refresh dependencies", e);
              }
            }
            setShowDeactivationModal(true);
          } else if (errorData.errorCode === 'REPLACEMENT_REQUIRED') {
            setDeactivationDependencies(errorData);
            setShowDeactivationModal(true);
          }
          return; // Stop form submission
        }
      }

      const msg = String(err?.response?.data?.message || err?.message || 'Unable to save user');
      const inline = mapCaregiverBackendError(msg);
      if (inline) {
        setErrors(prev => ({ ...prev, ...inline }));
      } else {
        toast.error(msg);
      }
    }

    // fetch(`${process.env.REACT_APP_SERVER_URL}` + "/users/create",
    //   {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenStr}` },
    //     body: userData
    //   })
    //   .then(response => response.json())
    //   .then(function (data) {
    //     const items = data;
    //     console.log("AddFacility " + items.message);
    //     if (items.message == "User created successfully") {
    //       toast.success('User added successfully', {
    //         theme: 'colored'
    //       });
    //       navigate(paths.viewFacility);
    //     }
    //     else {
    //       toast.error('Unable to submit User', {
    //         theme: 'colored'
    //       });
    //     }
    //   })

  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'username' && value.trim() === '') {
      setUsernameAvailable(null);
    }
    if (name === 'email' && value.trim() === '') {
      setEmailAvailable(null);
    }
  };

  const handleAccessFlagChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
  };

  const handleRoleFieldChange = (e, flag) => {
    const val = e.target.value;

    if (String(val) === "5") {
      setProgramDisabledFacility(false);
      setProgramDisabledGroup(false);
      // Only fetch day programs when the group has them enabled — otherwise
      // there's no Day Programs picker to populate and the call is wasted.
      if (Number(is_dayprogram_active) === 1 && dayProgramOptions.length === 0) {
        loadDayPrograms();
      }
    } else if (String(val) === "4") {
      setProgramDisabledFacility(true);
      setProgramDisabledGroup(false);
    } else if (String(val) === "6") {
      setProgramDisabledFacility(false);
      // setProgramDisabledGroup(true);
      // Fetch day programs if not already loaded (role 6 only exists in the
      // dropdown when is_dayprogram_active = 1, so the guard is belt-and-
      // suspenders here)
      if (Number(is_dayprogram_active) === 1 && dayProgramOptions.length === 0) {
        loadDayPrograms();
      }
    } else if (String(val) === "7") {
      // SharedLiving caregiver — pick ONE SL-flagged facility, then one
      // patient from it (UI-065/066). Facility list comes from
      // /facilities/list-sharedliving-facilities, not the generic pool.
      setProgramDisabledFacility(false);
      setProgramDisabledGroup(false);
      if (caregiverFacilityOptions.length === 0) {
        loadCaregiverFacilities(formData.group || locationData?.group_id);
      }
    } else if (val != undefined) {
      setProgramDisabledFacility(true);
      setProgramDisabledGroup(true);
    } else if (locationData == null) {
      setProgramDisabledFacility(true);
      setProgramDisabledGroup(true);
    }

    if (!flag) {
      setFormData({
        ...formData,
        [e.target.name]: val
      });
    }

    if (!flag) {
      setSelectedFacilities([]);
      setSelectedAvailable([]);
      setSelectedChecked([]);
      setSearchAvailable('');
      setSelectedDayPrograms([]);
      setSelectedAvailableDayPrograms([]);
      setSelectedCheckedDayPrograms([]);
      setSearchAvailableDayPrograms('');
      // SharedLiving patient transfer-list reset
      setPatientOptions([]);
      setSelectedPatients([]);
      setAtHomeRelationship('caregiver');
    }
  };

  const loadDayPrograms = async (groupIdOverride) => {
    try {
      setDayProgramsLoading(true);
      const tokenStr = localStorage.getItem("accessToken");
      const isGroupAdmin = Number(currentRoleId) === 4;
      const selectedGroupId =
        groupIdOverride ||
        locationData?.group_id ||
        formData.group ||
        localStorage.getItem("groupId");

      if (!isGroupAdmin && !selectedGroupId) {
        setDayProgramOptions([]);
        return [];
      }

      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/list`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      const dayProgramData =
        response.data?.data?.day_programs ||
        response.data?.day_programs ||
        response.data?.data ||
        response.data ||
        [];

      const options = normalizeDayProgramOptions(
        dayProgramData,
        isGroupAdmin ? null : selectedGroupId
      );

      setDayProgramOptions(options);
      return options;
    } catch (error) {
      console.error("Failed to fetch day programs", error);
      toast.error("Unable to load day programs.");
      setDayProgramOptions([]);
      return [];
    } finally {
      setDayProgramsLoading(false);
    }
  };

  // UI-065: load the SL-flagged facility list for the caregiver's group. Maps
  // the backend rows ({id, facility_name, ...}) to the local picker shape
  // ({key, value, group_id, ...}) so the existing ThemedSelect bindings keep
  // working.
  const loadCaregiverFacilities = async (groupIdOverride) => {
    const currentRole = Number(currentRoleId);
    // Group Admin → backend locks to their own group regardless of the param.
    // Pharmacy roles → pass the selected group when available.
    const effectiveGroupId =
      currentRole <= 3
        ? (groupIdOverride || formData.group || localStorage.getItem('groupId') || null)
        : null;

    setCaregiverFacilitiesLoading(true);
    try {
      const { data } = await fetchSharedLivingFacilities(effectiveGroupId || undefined);
      const mapped = (Array.isArray(data) ? data : [])
        .map(f => ({
          key: f.facility_name || `Facility ${f.id}`,
          value: f.id,
          group_id: f.group_id,
          external_facility_id: f.external_facility_id,
          is_sharedliving_facility: 1
        }))
        .sort((a, b) => a.key.localeCompare(b.key));
      setCaregiverFacilityOptions(mapped);
      return mapped;
    } catch (error) {
      console.error('Failed to fetch SharedLiving facilities', error);
      setCaregiverFacilityOptions([]);
      return [];
    } finally {
      setCaregiverFacilitiesLoading(false);
    }
  };

  // SharedLiving (role 7): fetch patients for the selected facility. Results
  // are cached per facility id (UI-018).
  const loadPatientsForFacilities = async (facilities) => {
    const facilityIds = (facilities || [])
      .map(getOptionValue)
      .filter(Boolean);

    if (facilityIds.length === 0) {
      setPatientOptions([]);
      return [];
    }

    setPatientsLoading(true);
    try {
      const tokenStr = localStorage.getItem("accessToken");
      const cache = patientCacheRef.current;
      const idsToFetch = facilityIds.filter(id => !cache[id]);

      await Promise.all(
        idsToFetch.map(async (facilityId) => {
          try {
            const { data } = await axios.get(
              `${process.env.REACT_APP_SERVER_URL}/patients/list-all-patients/${facilityId}`,
              { headers: { Authorization: `Bearer ${tokenStr}` } }
            );
            const objData = data?.patients?.data || [];
            cache[facilityId] = objData
              .filter(p => Number(p.is_active) === 1)
              .map(p => {
                const dobLabel = formatDob(p.dob);
                return {
                  key:
                    `${p.last_name}, ${p.first_name}` +
                    (dobLabel ? ` (${dobLabel})` : ''),
                  value: p.id,
                  facility_id: p.facility_id
                };
              });
          } catch (err) {
            cache[facilityId] = [];
          }
        })
      );

      // Merge + de-dupe across the selected facilities.
      const merged = [];
      const seen = new Set();
      facilityIds.forEach(id => {
        (cache[id] || []).forEach(patient => {
          const key = String(patient.value);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(patient);
          }
        });
      });
      merged.sort((a, b) => a.key.localeCompare(b.key));

      setPatientOptions(merged);
      return merged;
    } catch (error) {
      console.error("Failed to fetch patients", error);
      toast.error("Unable to load patients for the selected facilities.");
      setPatientOptions([]);
      return [];
    } finally {
      setPatientsLoading(false);
    }
  };

  // Role 7 facility dropdown change (single-select) → drive selectedFacilities
  // (which the effect below watches to reload the Available patient pool).
  // Changing facility clears selected patients so a caregiver is assigned from
  // one facility at a time.
  const handleCaregiverFacilityChange = (selected) => {
    setSelectedFacilities(selected ? [selected] : []);
    setPatientOptions([]);
    setSelectedPatients([]);
  };

  // Reactively reload the patient pool whenever the facility selection changes
  // for a role 7 caregiver.
  useEffect(() => {
    if (String(formData.role) !== "7") {
      return;
    }
    loadPatientsForFacilities(selectedFacilities);
  }, [selectedFacilities, formData.role]);

  // Edit mode (UI-021): pre-populate the caregiver's currently assigned
  // patients + the facilities those patients belong to so the admin sees the
  // full current state before diffing.
  const loadCaregiverAssignments = async (userId) => {
    if (!userId) return;
    try {
      const tokenStr = localStorage.getItem("accessToken");
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/athome/patients/${userId}`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      const assigned =
        data?.patients?.data ||
        data?.data ||
        data?.patients ||
        [];

      if (!Array.isArray(assigned) || assigned.length === 0) {
        return;
      }

      // Pre-select the facilities the assigned patients live at (so the patient
      // pool loads), including any hidden AtHome facility (UI-032).
      const facilityIds = [...new Set(assigned.map(p => String(p.facility_id)).filter(Boolean))];
      const facilityResults = JSON.parse(localStorage.getItem("facilitiesData")) || [];
      const preselectedFacilities = facilityResults.filter(f =>
        facilityIds.includes(getOptionValue(f))
      );
      if (preselectedFacilities.length > 0) {
        setSelectedFacilities([preselectedFacilities[0]]);
      }

      // Pre-select the patients themselves, limited to the selected facility.
      const selectedFacilityId = preselectedFacilities[0]?.value || facilityIds[0];
      const preselectedPatients = assigned
        .filter(p => String(p.facility_id) === String(selectedFacilityId))
        .map(p => {
        const dobLabel = formatDob(p.dob);
        return {
          key:
            `${p.last_name}, ${p.first_name}` +
            (dobLabel ? ` (${dobLabel})` : ''),
          value: p.patient_id,
          facility_id: p.facility_id
        };
      });
      setSelectedPatients(preselectedPatients);

      // Seed the relationship from the first assignment if present.
      const firstRelationship = assigned.find(p => p.relationship)?.relationship;
      if (firstRelationship) {
        setAtHomeRelationship(firstRelationship);
      }
    } catch (error) {
      console.error("Failed to load caregiver assignments", error);
    }
  };

  const handleFacilityFieldChange = (e) => {
    //console.log("Target " + Array.from(e.target.selectedOptions, option => option.value));
    // setFacilityValue(Array.from(e.target.selectedOptions, option => option.value));
    setSelectedFacilities(Array.from(e.target.selectedOptions, option => option.value));
  };

  const fetchDayPrograms = async (groupIdOverride) => loadDayPrograms(groupIdOverride);

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Add-User-Profile', href: paths.addFacility, isActive: true }
  ];

  const validate = () => {
    const newErrors = {};
    if (!formData.fname) {
      newErrors.fname = 'First Name is required.'
    } else if (!FormValidations(formData.fname, 1)) {
      newErrors.fname = 'Only letters are allowed';
    }
    if (!formData.lname) {
      newErrors.lname = 'Last Name is required.';
    } else if (!FormValidations(formData.lname, 1)) {
      newErrors.lname = 'Only letters are allowed';
    }
    // if (!formData.password) {
    //   newErrors.password = 'Password is required.';
    // } else if (!FormValidations(formData.password, 7, 6)) {
    //   newErrors.password = 'Password should be atleast 6 characters';
    // }
    if (!formData.cnfpassword) {
      newErrors.cnfpassword = 'Confirm Password is required.';
    } else if (formData.cnfpassword !== formData.password) {
      newErrors.cnfpassword = 'Passwrord must Match';
    }
    if (!formData.role) newErrors.role = 'Role is required.';
    if (formData.mobile && !FormValidations(formData.mobile, 6, 10)) {
      newErrors.mobile = 'Invalid mobile number format.';
    }
    if (formData.city && !FormValidations(formData.city, 1)) {
      newErrors.city = 'City should be in letters';
    }
    if (!formData.username) {
      newErrors.username = 'Username is required.';
    } else if ((locationData == null || locationData == undefined) && !FormValidations(formData.username, 2)) {
      newErrors.username = 'Invalid username format.';
    }
    if (!formData.email) {
      newErrors.email = 'Email is required.';
    } else if (!FormValidations(formData.email, 5)) {
      newErrors.email = 'Invalid email format.';
    }

    if (String(formData.role) === "5") {
      if (!formData.group) {
        newErrors.group = 'Group is required for Facility Staff.';
      }
      if (!selectedFacilities || selectedFacilities.length === 0) {
        newErrors.facility = 'At least one Facility must be selected for Facility Staff.';
      }
    } else if (String(formData.role) === "6") {
      if (!selectedFacilities || selectedFacilities.length === 0) {
        newErrors.facility = 'At least one Day Program must be selected for Day Program User.';
      }
    } else if (String(formData.role) === "7") {
      if (JSON.parse(localStorage.getItem("roleId")) < 4 && !formData.group) {
        newErrors.group = 'Group is required for SharedLiving Caregiver.';
      }
      if (!selectedFacilities || selectedFacilities.length === 0) {
        newErrors.facility = 'Select a SharedLiving facility to pick a patient from.';
      }
      // Backend SL-025a/SL-025b: a caregiver cannot exist without an assigned
      if (!selectedPatients || selectedPatients.length === 0) {
        newErrors.patients = 'Select at least one patient to assign to this caregiver.';
      }
    } else if (String(formData.role) === "4") {
      if (!formData.group) {
        newErrors.group = 'Group is required for Facility Admin.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAvailableCheck = (e) => {
    const val = String(e.target.value);
    setSelectedAvailable(prev =>
      e.target.checked ? [...prev, val] : prev.filter(v => v !== val)
    );
  };

  const handleSelectedCheck = (e) => {
    const val = String(e.target.value);
    setSelectedChecked(prev =>
      e.target.checked ? [...prev, val] : prev.filter(v => v !== val)
    );
  };

  const handleAvailableDayProgramCheck = (e) => {
    const val = String(e.target.value);
    setSelectedAvailableDayPrograms(prev =>
      e.target.checked ? [...prev, val] : prev.filter(v => v !== val)
    );
  };

  const handleSelectedDayProgramCheck = (e) => {
    const val = String(e.target.value);
    setSelectedCheckedDayPrograms(prev =>
      e.target.checked ? [...prev, val] : prev.filter(v => v !== val)
    );
  };


  const addToSelected = () => {
    const source = String(formData.role) === "6" ? dayProgramOptions : programOptions;
    const toAdd = source.filter(f => selectedAvailable.includes(getOptionValue(f)));
    const updated = [
      ...selectedFacilities,
      ...toAdd.filter(f => !selectedFacilities.some(selected => getOptionValue(selected) === getOptionValue(f)))
    ];
    setSelectedFacilities(updated);
    setSelectedAvailable([]);
  };

  const removeFromSelected = () => {
    const remaining = selectedFacilities.filter(f => !selectedChecked.includes(getOptionValue(f)));
    setSelectedFacilities(remaining);
    setSelectedChecked([]);
  };

  const addDayProgramsToSelected = () => {
    const toAdd = dayProgramOptions.filter(program =>
      selectedAvailableDayPrograms.includes(getOptionValue(program))
    );
    const updated = [
      ...selectedDayPrograms,
      ...toAdd.filter(program => !selectedDayPrograms.some(selected => getOptionValue(selected) === getOptionValue(program)))
    ];
    setSelectedDayPrograms(updated);
    setSelectedAvailableDayPrograms([]);
  };

  const removeDayProgramsFromSelected = () => {
    const remaining = selectedDayPrograms.filter(program =>
      !selectedCheckedDayPrograms.includes(getOptionValue(program))
    );
    setSelectedDayPrograms(remaining);
    setSelectedCheckedDayPrograms([]);
  };

  const handleCaregiverPatientChange = (selected) => {
    setSelectedPatients(selected || []);
  };

  const renderTransferList = ({
    label,
    required = true,
    source,
    selectedItems,
    searchValue,
    setSearchValue,
    selectedAvailableItems,
    onAvailableChange,
    selectedCheckedItems,
    onSelectedChange,
    onAdd,
    onRemove,
    errorKey,
    loading = dayProgramsLoading,
    disabled = programDisableFacility
  }) => {
    const filteredAvailableItems = source
      .filter(option => !selectedItems.some(selected => getOptionValue(selected) === getOptionValue(option)))
      .filter(option => option.key.toLowerCase().includes(searchValue.toLowerCase()));

    return (
      <Form.Group>
        <Form.Label className={required ? "required" : ""}>{label}</Form.Label>
        <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch">
          <div className="border rounded p-2" style={{ flex: '1 1 0', minWidth: 0 }}>
            <div className="mb-2 fw-semibold">Available</div>
            <Form.Control
              className='mb-2'
              type="text"
              size="sm"
              placeholder="Search"
              disabled={disabled}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ width: '100%' }}
            />
           <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
  {loading ? (
    <div className="text-center py-3">
      <span className="spinner-border spinner-border-sm me-2"></span>
      Loading data...
    </div>
  ) : filteredAvailableItems.length > 0 ? (
    filteredAvailableItems.map(item => (
      <div key={item.value}>
        <Form.Check
          type="checkbox"
          id={`${label}-available-${item.value}`}
          label={item.key}
          value={item.value}
          disabled={disabled}
          checked={selectedAvailableItems.includes(getOptionValue(item))}
          onChange={onAvailableChange}
        />
      </div>
    ))
  ) : (
    <div className="text-muted text-center py-2">No data found</div>
  )}
</div>
          </div>

          <div
            className="d-flex flex-column justify-content-center gap-2"
            style={{ flex: '0 0 auto' }}
          >
            <Button variant="outline-primary" size="sm" disabled={disabled} onClick={onAdd}>&gt;&gt;</Button>
            <Button variant="outline-secondary" size="sm" disabled={disabled} onClick={onRemove}>&lt;&lt;</Button>
          </div>

          <div className="border rounded p-2" style={{ flex: '1 1 0', minWidth: 0 }}>
            <div className="mb-2 fw-semibold">Selected</div>
            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
              {selectedItems.map(item => (
                <div key={item.value}>
                  <Form.Check
                    type="checkbox"
                    id={`${label}-selected-${item.value}`}
                    label={item.key}
                    disabled={disabled}
                    value={item.value}
                    checked={selectedCheckedItems.includes(getOptionValue(item))}
                    onChange={onSelectedChange}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <Form.Control.Feedback type="invalid" className="d-block">
          {errors[errorKey]}
        </Form.Control.Feedback>
      </Form.Group>
    );
  };

  // Renders the role-specific assignment controls (day programs / facilities /
  // SharedLiving patients). Shared between the create and edit layouts so the
  // two never drift apart.
  const renderRoleAssignmentSection = () => {
    if (String(formData.role) === "6") {
      return renderTransferList({
        label: 'Day Programs',
        required: true,
        source: dayProgramOptions,
        selectedItems: selectedFacilities,
        searchValue: searchAvailable,
        setSearchValue: setSearchAvailable,
        selectedAvailableItems: selectedAvailable,
        onAvailableChange: handleAvailableCheck,
        selectedCheckedItems: selectedChecked,
        onSelectedChange: handleSelectedCheck,
        onAdd: addToSelected,
        onRemove: removeFromSelected,
        errorKey: 'facility'
      });
    }

    if (String(formData.role) === "7") {
      const emptyFacilityList =
        !caregiverFacilitiesLoading && caregiverFacilityOptions.length === 0;
      return (
        <div className="d-flex flex-column gap-3">
          {emptyFacilityList && (
            <div className="alert alert-warning py-2 mb-0">
              <strong>No SharedLiving facilities are configured for this group.</strong>
              <div className="fs-11 mt-1">
                Ask your pharmacy admin to flag a facility before creating caregivers.
              </div>
            </div>
          )}
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Facility</Form.Label>
                <ThemedSelect
                  placeholder={
                    caregiverFacilitiesLoading
                      ? 'Loading SharedLiving facilities...'
                      : 'Search a SharedLiving facility'
                  }
                  classNamePrefix="react-select"
                  className={errors.facility ? 'is-invalid' : ''}
                  options={caregiverFacilityOptions}
                  value={selectedFacilities[0] || null}
                  getOptionLabel={option => option.key}
                  getOptionValue={option => String(option.value)}
                  onChange={handleCaregiverFacilityChange}
                  isClearable
                  isSearchable
                  isLoading={caregiverFacilitiesLoading}
                  isDisabled={emptyFacilityList}
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.facility}
                </Form.Control.Feedback>
                <Form.Text muted>
                  Only SharedLiving-flagged facilities are shown.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Patient</Form.Label>
                <ThemedSelect
                  placeholder={
                    !selectedFacilities.length
                      ? 'Select a facility first'
                      : patientsLoading
                      ? 'Loading patients...'
                      : 'Search a patient'
                  }
                  classNamePrefix="react-select"
                  className={errors.patients ? 'is-invalid' : ''}
                  options={patientOptions}
                  value={selectedPatients}
                  getOptionLabel={option => option.key}
                  getOptionValue={option => String(option.value)}
                  onChange={handleCaregiverPatientChange}
                  isMulti
                  closeMenuOnSelect={false}
                  isSearchable
                  isLoading={patientsLoading}
                  isDisabled={!selectedFacilities.length || patientsLoading}
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.patients}
                </Form.Control.Feedback>
                <Form.Text muted>
                  Select one or more patients for this caregiver.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </div>
      );
    }

    // Roles 5 (facility staff) and anything else: facilities + optional day
    // programs for role 5.
    return (
      <div className="d-flex flex-column gap-3">
        {renderTransferList({
          label: 'Program Facilities',
          required: true,
          source: programOptions,
          selectedItems: selectedFacilities,
          searchValue: searchAvailable,
          setSearchValue: setSearchAvailable,
          selectedAvailableItems: selectedAvailable,
          onAvailableChange: handleAvailableCheck,
          selectedCheckedItems: selectedChecked,
          onSelectedChange: handleSelectedCheck,
          onAdd: addToSelected,
          onRemove: removeFromSelected,
          errorKey: 'facility'
        })}
        {String(formData.role) === "5" && (
          <Form.Group className='mb-3'>
            <Form.Label>Access</Form.Label>
            <div className="d-flex flex-column flex-sm-row gap-3">
              <Form.Check
                type="switch"
                id="has_web_access"
                name="has_web_access"
                label="Web Access"
                checked={Boolean(Number(formData.has_web_access))}
                onChange={handleAccessFlagChange}
              />
              <Form.Check
                type="switch"
                id="has_mobile_access"
                name="has_mobile_access"
                label="Mobile Access"
                checked={Boolean(Number(formData.has_mobile_access))}
                onChange={handleAccessFlagChange}
              />
            </div>
          </Form.Group>
        )}
        {/* Day Programs picker for Facility Staff (role 5) — hidden entirely
            when the group has is_dayprogram_active = 0. Same flag that gates
            the "Day Program User" entry in the role dropdown above. */}
        {String(formData.role) === "5" && Number(is_dayprogram_active) === 1 &&
          renderTransferList({
            label: 'Day Programs',
            required: false,
            source: dayProgramOptions,
            selectedItems: selectedDayPrograms,
            searchValue: searchAvailableDayPrograms,
            setSearchValue: setSearchAvailableDayPrograms,
            selectedAvailableItems: selectedAvailableDayPrograms,
            onAvailableChange: handleAvailableDayProgramCheck,
            selectedCheckedItems: selectedCheckedDayPrograms,
            onSelectedChange: handleSelectedDayProgramCheck,
            onAdd: addDayProgramsToSelected,
            onRemove: removeDayProgramsFromSelected,
            errorKey: 'day_programs'
          })}
      </div>
    );
  };


  const handleIsActiveChange = async (e) => {
    const val = e.target.checked;
    
    // Deactivation flow for role 5
    if (!val && String(formData.role) === "5" && locationData?.id) {
      setIsActiveLoading(true);
      try {
        const response = await fetchUserDependentAssignments(locationData.id);
        if (response.has_any) {
          setDeactivationDependencies(response.dependencies);
          setShowDeactivationModal(true);
          setDeactivationServerErrors([]);
          setStaleCandidateBanner(false);
          // Do NOT update formData.is_active yet. Wait for modal confirm.
          return;
        }
      } catch (err) {
        console.error("Failed to fetch dependent assignments", err);
        toast.error("Failed to check patient assignments.");
      } finally {
      setIsActiveLoading(false);  // ← stop
    }
    }
    
    setFormData({
      ...formData,
      is_active: val
    });
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (assignmentReplacements) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [assignmentReplacements]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isSavingRef.current && !!assignmentReplacements && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      const confirmLeave = window.confirm("You have unsaved replacements for the deactivated user. Are you sure you want to leave without saving? Your changes will be lost.");
      if (confirmLeave) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  return (
    <>
      <DeactivationReplacementModal
        show={showDeactivationModal}
        dependencies={deactivationDependencies}
        serverErrors={deactivationServerErrors}
        staleCandidateBanner={staleCandidateBanner}
        onCancel={() => {
          setShowDeactivationModal(false);
          setAssignmentReplacements(null);
          // Toggle stays in its active state
        }}
        onConfirm={(replacements) => {
          setAssignmentReplacements(replacements);
          setShowDeactivationModal(false);
          setFormData({
            ...formData,
            is_active: false
          });
        }}
        userId={locationData?.id}
        userName={formData.fname + ' ' + formData.lname}
      />
      {locationData ? [] : <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />}
      <Form onSubmit={onSubmit} name="facilityform">
        {locationData != null ?
          <Row className="g-3">
            <Col>
              <div className="d-flex align-items-center">
                <Form.Check
                  type='switch'
                  name="is_active"
                  id='is_active'
                  className="fs-5 mb-0"
                  checked={formData.is_active}
                  onChange={handleIsActiveChange}
                  disabled={isActiveLoading}   // ← disable during load
                  label={
                    isActiveLoading
                      ? <>
                        Is Active
                        <span
                          className="spinner-border spinner-border-sm ms-2"
                          role="status"
                          aria-hidden="true"
                        />
                      </>
                      : 'Is Active'
                  }
                />
                {/* {assignmentReplacements && !formData.is_active && (
                  <span className="text-danger ms-3 fs-6">
                    (Unsaved replacements – don't forget to save!)
                  </span>
                )} */}
              </div>
            </Col>
            <Col lg={12}>
              <div className="mb-2 border" style={{ borderColor: '#f2f2f3' }}>
                <Card.Header as="h6">
                  Personal Information
                </Card.Header>
                <Card.Body>
                  <Row className="gx-2 gy-3 mb-2">
                    <Col md="6">
                      <Form.Group>
                        <Form.Label className="required">First Name</Form.Label>
                        <Form.Control
                          placeholder='First Name'
                          value={formData.fname}
                          name="fname"
                          onChange={handleFieldChange}
                          type="text"
                          size="sm"
                          isInvalid={!!errors.fname}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.fname}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md="6">
                      <Form.Group>
                        <Form.Label className="required">Last Name</Form.Label>
                        <Form.Control
                          placeholder='Last Name'
                          value={formData.lname}
                          name="lname"
                          onChange={handleFieldChange}
                          type="text"
                          size="sm"
                          isInvalid={!!errors.lname}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.lname}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="gx-2 gy-3 mb-2">
                    <Col md="6">
                      <Form.Group>
                        <Form.Label>Mobile</Form.Label>
                        <MaskedInput
                          mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                          className={`form-control form-control-sm ${errors.mobile ? 'is-invalid' : ''
                            }`}
                          guide={false}
                          placeholder="(XXX) XXX-XXXX"
                          onChange={handleFieldChange}
                          size="sm"
                          name="mobile"
                          value={formData.mobile}
                        />
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {errors.mobile}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md="6">
                      <Form.Group>
                        <Form.Label className='required'>Email</Form.Label>
                        <Form.Control
                          placeholder='Email'
                          value={formData.email}
                          name="email"
                          onChange={handleFieldChange}
                          type="text"
                          size="sm"
                          isInvalid={!!errors.email}
                          disabled={locationData != null}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="gx-2 gy-3">
                    <Col md="6">
                      <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          placeholder='Address'
                          value={formData.address}
                          name="address"
                          onChange={handleFieldChange}
                          type="text"
                          size="sm"
                        />
                      </Form.Group>
                    </Col>
                    <Col md="6">
                      <Form.Group>
                        <Form.Label>Address2</Form.Label>
                        <Form.Control
                          placeholder='Address2'
                          value={formData.address2}
                          name="address2"
                          onChange={handleFieldChange}
                          type="text"
                          size="sm"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="gx-2 gy-3 mb-2">
                    <Col md="4">
                      <Form.Group>
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          placeholder='City'
                          Value={formData.city}
                          name="city"
                          onChange={handleFieldChange}
                          type="text"
                          size="sm"
                          isInvalid={!!errors.city}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.city}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md="4">
                      <Form.Group>
                        <Form.Label>State</Form.Label>
                        <Form.Select
                          name="state"
                          onChange={handleFieldChange}
                          size="sm"
                        //disabled={watch("role") == 4 ? true : false}
                        >
                          {stateoptions.map((stateoption) => {
                            let isStateSelected = false;
                            if (locationData != null) {
                              if (stateoption.value == formData.state) {
                                isStateSelected = "selected";
                              }
                            }
                            return (
                              <option key={stateoption.value} value={stateoption.value} selected={isStateSelected}>
                                {stateoption.key}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md="4">
                      <Form.Group>
                        <Form.Label>Zip</Form.Label>
                        <Form.Control
                          placeholder="Zip"
                          value={formData.zip}
                          name="zip"
                          onChange={handleFieldChange}
                          type="text"
                          size="sm"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                </Card.Body>
              </div>
            </Col>
            <Col xs={12}>
              <div className="mb-2 border" style={{ borderColor: '#f2f2f3' }}>
                <Card.Header as="h6">
                  Access Information
                </Card.Header>
                <Card.Body>
                  <Row className="gx-2 gy-3 mb-2">
                    <Col md="6">
                      <Form.Group>
                        <Form.Label className="required">User Name</Form.Label>
                        <Form.Control
                          placeholder='User Name'
                          value={formData.username}
                          name="username"
                          onChange={handleFieldChange}
                          type="text"
                          size="sm"
                          disabled={locationData != null}
                          isInvalid={!!errors.username}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.username}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6} className="d-flex flex-column h-100">
                      {/* Hybrid: pharmacy admins see Group BEFORE Role so the
                          role list can reflect the target group's flags. Role
                          4+ never sees the Group dropdown (locked to own group). */}
                      {JSON.parse(localStorage.getItem("roleId")) < 4 ? <Form.Group className='mb-3 flex-grow-1'>
                        <Form.Label>Group</Form.Label>
                        <Form.Select
                          name="group"
                          value={formData.group || ''}
                          onChange={handleGroupChange}
                          size="sm"
                          isInvalid={!!errors.group}
                        >
                          {groupOptions.map((groupoption) => {
                            let isRoleSelected = false;
                            if (locationData != null) {
                              if (groupoption.value == formData.group) {
                                isRoleSelected = "selected";
                              }
                            }
                            return (<option key={groupoption.value} value={groupoption.value} selected={isRoleSelected}>
                              {groupoption.key}
                            </option>)
                          })}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.group}</Form.Control.Feedback>
                      </Form.Group> : []}
                      <Form.Group className='flex-grow-1'>
                        <Form.Label className="required">Role</Form.Label>
                        <Form.Select
                          name="role"
                          value={formData.role || ''}
                          onChange={handleRoleFieldChange}
                          size="sm"
                          isInvalid={!!errors.role}
                        >
                          {roleoptions.map((roleoption) => {
                            let isRoleSelected = false;
                            if (locationData != null) {
                              if (roleoption.value == formData.role) {
                                isRoleSelected = "selected";
                              }
                            }
                            return (
                              <option
                                key={roleoption.value}
                                value={roleoption.value}
                                selected={isRoleSelected}
                                disabled={!!roleoption.disabled}
                              >
                                {roleoption.key}
                              </option>
                            );
                          })}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.role}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="gx-3 gy-3">
                    <Col md={12}>
                      {renderRoleAssignmentSection()}
                    </Col>
                  </Row>
                  {/* <Row className="gx-2 gy-3">
                    <Col md="6">
                      <Form.Group>
                        <Form.Label className="required">Password</Form.Label>
                        <Form.Control
                          placeholder='Password'
                          value={formData.password}
                          name="password"
                          onChange={handleFieldChange}
                          type="password"
                          size="sm"
                          isInvalid={!!errors.password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.password}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md="6">
                      <Form.Group>
                        <Form.Label className="required">Confirm Password</Form.Label>
                        <Form.Control
                          placeholder='Confirm Password'
                          value={formData.cnfpassword}
                          name="cnfpassword"
                          onChange={handleFieldChange}
                          type="password"
                          size="sm"
                          isInvalid={!!errors.cnfpassword}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.cnfpassword}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row> */}
                </Card.Body>
              </div>
            </Col>
            <Col xs={12}>
              <Row className="flex-between-center">
                <Col></Col>
                <Col xs="auto" >
                  <Button variant="primary"
                    type="submit"
                  >
                    {locationData != null ? mapYourMedsConstant.UPDATE_ACTION : mapYourMedsConstant.SAVE_ACTION}
                  </Button>
                </Col>
                <Col>
                  <Form.Label><span className="text-danger">*</span> indicates required field</Form.Label>
                </Col>
              </Row>
            </Col>
          </Row>
          :
          <Card>
            <Row className="g-3 p-3">
              <Col lg={12}>
                <div className="mb-2 border" style={{ borderColor: '#f2f2f3' }}>
                  <Card.Header as="h6">
                    Personal Information
                  </Card.Header>
                  <Card.Body>
                    <Row className="gx-2 gy-3 mb-2">
                      <Col md="6">
                        <Form.Group>
                          <Form.Label className="required">First Name</Form.Label>
                          <Form.Control
                            placeholder='First Name'
                            value={formData.fname}
                            name="fname"
                            onChange={handleFieldChange}
                            type="text"
                            size="sm"
                            isInvalid={!!errors.fname}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.fname}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md="6">
                        <Form.Group>
                          <Form.Label className="required">Last Name</Form.Label>
                          <Form.Control
                            placeholder='Last Name'
                            value={formData.lname}
                            name="lname"
                            onChange={handleFieldChange}
                            type="text"
                            size="sm"
                            isInvalid={!!errors.lname}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.lname}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="gx-2 gy-3 mb-2">
                      <Col md="6">
                        <Form.Group>
                          <Form.Label>Mobile</Form.Label>
                          <MaskedInput
                            mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                            className={`form-control form-control-sm ${errors.mobile ? 'is-invalid' : ''
                              }`}
                            guide={false}
                            placeholder="(XXX) XXX-XXXX"
                            onChange={handleFieldChange}
                            size="sm"
                            name="mobile"
                            value={formData.mobile}
                          />
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {errors.mobile}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md="6">
                        <Form.Group>
                          <Form.Label className='required'>Email</Form.Label>
                          <Form.Control
                            name="email"
                            value={formData.email}
                            onChange={handleFieldChange}
                            isInvalid={!!errors.email || emailAvailable === false}
                          />
                          {checkingEmail && <small className="text-primary">Checking...</small>}
                          {!checkingEmail && emailAvailable === true && <small className="text-success">Email is available ✅</small>}
                          <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="gx-2 gy-3">
                      <Col md="6">
                        <Form.Group>
                          <Form.Label>Address</Form.Label>
                          <Form.Control
                            placeholder='Address'
                            value={formData.address}
                            name="address"
                            onChange={handleFieldChange}
                            type="text"
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                      <Col md="6">
                        <Form.Group>
                          <Form.Label>Address2</Form.Label>
                          <Form.Control
                            placeholder='Address2'
                            value={formData.address2}
                            name="address2"
                            onChange={handleFieldChange}
                            type="text"
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="gx-2 gy-3 mb-2">
                      <Col md="4">
                        <Form.Group>
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            placeholder='City'
                            Value={formData.city}
                            name="city"
                            onChange={handleFieldChange}
                            type="text"
                            size="sm"
                            isInvalid={!!errors.city}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.city}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md="4">
                        <Form.Group>
                          <Form.Label>State</Form.Label>
                          <Form.Select
                            name="state"
                            onChange={handleFieldChange}
                            size="sm"
                          //disabled={watch("role") == 4 ? true : false}
                          >
                            {stateoptions.map((stateoption) => {
                              let isStateSelected = false;
                              if (locationData != null) {
                                if (stateoption.value == formData.state) {
                                  isStateSelected = "selected";
                                }
                              }
                              return (
                                <option key={stateoption.value} value={stateoption.value} selected={isStateSelected}>
                                  {stateoption.key}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md="4">
                        <Form.Group>
                          <Form.Label>Zip</Form.Label>
                          <Form.Control
                            placeholder="Zip"
                            value={formData.zip}
                            name="zip"
                            onChange={handleFieldChange}
                            type="text"
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="gx-2 gy-3 mb-2">

                    </Row>
                  </Card.Body>
                </div>
              </Col>
              <Col xs={12}>
                <div className="mb-2 border" style={{ borderColor: '#f2f2f3' }}>
                  <Card.Header as="h6">
                    Access Information
                  </Card.Header>
                  <Card.Body>
                    <Row className="gx-2 gy-3 mb-2">
                      <Col md="12">
                        <Form.Group>
                          <Form.Label className="required">User Name</Form.Label>
                          <Form.Control
                            name="username"
                            value={formData.username}
                            onChange={handleFieldChange}
                            isInvalid={!!errors.username || usernameAvailable === false}
                          />
                          {checkingUsername && <small className="text-primary">Checking...</small>}
                          {usernameAvailable && <small className="text-success">Username is available ✅</small>}
                          <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="gx-3 gy-3 mb-2">
                      <Col md={6} className="d-flex flex-column h-100">
                        {/* Hybrid: pharmacy admins see Group BEFORE Role so the
                            role list can reflect the target group's flags. */}
                        {JSON.parse(localStorage.getItem("roleId")) < 4 ? <Form.Group className='mb-3 flex-grow-1'>
                          <Form.Label>Group</Form.Label>
                          <Form.Select
                            name="group"
                            value={formData.group || ''}
                            onChange={handleGroupChange}
                            size="sm"
                            isInvalid={!!errors.group}
                          >
                            {groupOptions.map((groupoption) => {
                              let isRoleSelected = false;
                              if (locationData != null) {
                                if (groupoption.value == formData.group) {
                                  isRoleSelected = "selected";
                                }
                              }
                              return (<option key={groupoption.value} value={groupoption.value} selected={isRoleSelected}>
                                {groupoption.key}
                              </option>)
                            })}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{errors.group}</Form.Control.Feedback>
                        </Form.Group> : []}
                        <Form.Group className='flex-grow-1'>
                          <Form.Label className="required">Role</Form.Label>
                          <Form.Select
                            name="role"
                            value={formData.role || ''}
                            onChange={handleRoleFieldChange}
                            size="sm"
                            isInvalid={!!errors.role}
                          >
                            {roleoptions.map((roleoption) => {
                              let isRoleSelected = false;
                              if (locationData != null) {
                                if (roleoption.value == formData.role) {
                                  isRoleSelected = "selected";
                                }
                              }
                              return (
                                <option
                                  key={roleoption.value}
                                  value={roleoption.value}
                                  selected={isRoleSelected}
                                  disabled={!!roleoption.disabled}
                                >
                                  {roleoption.key}
                                </option>
                              );
                            })}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.role}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="gx-3 gy-3">
                      <Col md={12}>
                        {renderRoleAssignmentSection()}
                      </Col>
                    </Row>
                    <Row className="gx-2 gy-3">
                      <Col md="6">
                        <Form.Group>
                          <Form.Label className="required">Password</Form.Label>
                          <Form.Control
                            placeholder='Password'
                            value={formData.password}
                            name="password"
                            onChange={handleFieldChange}
                            type="password"
                            size="sm"
                            isInvalid={!!errors.password}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md="6">
                        <Form.Group>
                          <Form.Label className="required">Confirm Password</Form.Label>
                          <Form.Control
                            placeholder='Confirm Password'
                            value={formData.cnfpassword}
                            name="cnfpassword"
                            onChange={handleFieldChange}
                            type="password"
                            size="sm"
                            isInvalid={!!errors.cnfpassword}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.cnfpassword}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </div>
              </Col>
              <Col xs={12}>
                <Row className="flex-between-center">
                  <Col></Col>
                  <Col xs="auto" >
                    <Button variant="primary"
                      type="submit"
                    >
                      {locationData != null ? mapYourMedsConstant.UPDATE_ACTION : mapYourMedsConstant.SAVE_ACTION}
                    </Button>
                  </Col>
                  <Col>
                    <Form.Label><span className="text-danger">*</span> indicates required field</Form.Label>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>}
      </Form>
    </>
  );
};

export default AddFacilityStaff;
